import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Eye, 
  BookOpen, 
  FileText,
  CheckCircle2,
  Clock,
  User,
  Users,
  Settings,
  ToggleLeft,
  ToggleRight,
  Trash2,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  AlertTriangle,
  CreditCard,
  Loader2,
  Building2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FirstSaleTips } from '@/components/instructor/FirstSaleTips';
import { EarningsToggle } from '@/components/instructor/EarningsToggle';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const InstructorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instructor, setInstructor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Submerchant state
  const [submerchantStatus, setSubmerchantStatus] = useState(null);
  const [showSubmerchantModal, setShowSubmerchantModal] = useState(false);
  const [submerchantLoading, setSubmerchantLoading] = useState(false);
  const [submerchantForm, setSubmerchantForm] = useState({
    submerchant_type: 'PERSONAL',
    full_name: '',
    identity_number: '',
    email: '',
    gsm_number: '',
    address: '',
    iban: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch instructor profile, courses, earnings and submerchant status in parallel
      const [profileRes, coursesRes, earningsRes, submerchantRes] = await Promise.all([
        axios.get(`${API}/instructor/me`, config),
        axios.get(`${API}/instructor/courses`, config),
        axios.get(`${API}/instructor/earnings`, config).catch(() => ({ data: null })),
        axios.get(`${API}/instructor/submerchant-status`, config).catch(() => ({ data: null }))
      ]);

      setInstructor(profileRes.data);
      setCourses(coursesRes.data);
      setEarnings(earningsRes.data);
      setSubmerchantStatus(submerchantRes.data);
      
      // Pre-fill form with user data
      if (profileRes.data?.user) {
        setSubmerchantForm(prev => ({
          ...prev,
          full_name: profileRes.data.user.full_name || '',
          email: profileRes.data.user.email || ''
        }));
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim için onaylı eğitmen olmanız gerekiyor');
        navigate('/application-status');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Veri yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmerchantSubmit = async () => {
    // Validation
    if (!submerchantForm.full_name || !submerchantForm.identity_number || 
        !submerchantForm.email || !submerchantForm.address || !submerchantForm.iban) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    
    // IBAN validation
    const iban = submerchantForm.iban.replace(/\s/g, '').toUpperCase();
    if (!iban.startsWith('TR') || iban.length !== 26) {
      toast.error('Geçersiz IBAN. TR ile başlamalı ve 26 karakter olmalı');
      return;
    }
    
    // Identity number validation
    if (submerchantForm.identity_number.length !== 11) {
      toast.error('TC Kimlik No 11 haneli olmalı');
      return;
    }
    
    setSubmerchantLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/instructor/onboard-submerchant`,
        submerchantForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Alt üye işyeri kaydınız oluşturuldu!');
      setShowSubmerchantModal(false);
      
      // Refresh submerchant status
      const statusRes = await axios.get(`${API}/instructor/submerchant-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmerchantStatus(statusRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kayıt oluşturulamadı');
    } finally {
      setSubmerchantLoading(false);
    }
  };

  const handleToggleStatus = async (courseId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/instructor/courses/${courseId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(newStatus === 'published' ? 'Kurs yayınlandı!' : 'Kurs taslağa alındı');
      fetchDashboardData();
    } catch (error) {
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/instructor/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Kurs başarıyla silindi');
      setDeleteConfirm(null);
      fetchDashboardData();
    } catch (error) {
      toast.error('Kurs silinirken hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      pilates: 'Pilates',
      yoga: 'Yoga',
      meditation: 'Meditasyon',
      fitness: 'Fitness',
      breathing: 'Nefes',
      mobility: 'Mobilite'
    };
    return labels[category] || category;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Başlangıç',
      intermediate: 'Orta',
      advanced: 'İleri'
    };
    return labels[level] || level;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!instructor) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <CardTitle>Erişim Reddedildi</CardTitle>
              <CardDescription>
                Bu sayfaya erişmek için onaylı bir eğitmen olmanız gerekiyor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/become-instructor')} className="btn-primary">
                Eğitmen Başvurusu Yap
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Hoş Geldiniz, {instructor?.user?.full_name || 'Eğitmen'}! 👋
            </h1>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Eğitmen hesabı aktif
              </Badge>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/instructor/students')}>
              <Users className="h-4 w-4 mr-1" />
              Öğrenciler
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/instructor/calendar')}>
              <Clock className="h-4 w-4 mr-1" />
              Takvim
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/instructor/analytics')}>
              <TrendingUp className="h-4 w-4 mr-1" />
              Analitik
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/instructor/referral')}>
              <ShoppingCart className="h-4 w-4 mr-1" />
              Referans
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/instructor/profile')}>
              <Settings className="h-4 w-4 mr-1" />
              Profil
            </Button>
            <Button className="btn-primary" size="sm" onClick={() => navigate('/instructor/courses/new')}>
              <Plus className="h-4 w-4 mr-1" />
              Yeni Kurs
            </Button>
          </div>
        </div>

        {/* Submerchant Warning Banner */}
        {submerchantStatus && !submerchantStatus.has_submerchant && (
          <Card className="mb-8 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-orange-800">Ödeme Alabilmek İçin Kayıt Gerekli</h3>
                    <p className="text-sm text-orange-700 mt-1">
                      Ders satışı yapabilmeniz için iyzico alt üye işyeri kaydınızı tamamlamanız gerekiyor. 
                      Bu kayıt, ödemelerinizin doğrudan banka hesabınıza aktarılmasını sağlar.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowSubmerchantModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Ödeme Kaydı Oluştur
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submerchant Active Badge */}
        {submerchantStatus?.has_submerchant && (
          <Card className="mb-8 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">Ödeme Kaydınız Aktif</h3>
                  <p className="text-sm text-green-700">
                    Ders satışlarınızdan elde ettiğiniz gelirler otomatik olarak banka hesabınıza aktarılacaktır.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Toplam Kurs</CardTitle>
              <BookOpen className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{instructor?.stats?.total_courses || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Oluşturduğunuz kurslar</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Yayında</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{instructor?.stats?.published_courses || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Satışta olanlar</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Taslak</CardTitle>
              <FileText className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{instructor?.stats?.draft_courses || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Yayınlanmamış</p>
            </CardContent>
          </Card>

          {/* Earnings Toggle - Son 7/30 gün */}
          <div className="lg:col-span-3">
            <EarningsToggle earnings={earnings} />
          </div>
        </div>

        {/* First Sale Tips - Satış yoksa göster */}
        {(!earnings || earnings.total_sales === 0) && (
          <div className="mb-8">
            <FirstSaleTips />
          </div>
        )}

        {/* My Courses Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Kurslarım</CardTitle>
                <CardDescription>Tüm kurslarınızı buradan yönetebilirsiniz</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {courses.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kurs Başlığı</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead>Seviye</TableHead>
                      <TableHead>Fiyat</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Son Güncelleme</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courses.map((course) => (
                      <TableRow key={course.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {course.thumbnail_url ? (
                              <img 
                                src={`${API}${course.thumbnail_url}`} 
                                alt={course.title}
                                className="w-12 h-12 rounded object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center">
                                <BookOpen className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{course.title}</p>
                              {course.subtitle && (
                                <p className="text-sm text-gray-500">{course.subtitle}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{getCategoryLabel(course.category)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getLevelLabel(course.level)}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">{course.price} TL</TableCell>
                        <TableCell>
                          {course.status === 'published' ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Yayında
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Taslak
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(course.updated_at || course.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(course.id, course.status)}
                              title={course.status === 'published' ? 'Taslağa Al' : 'Yayınla'}
                            >
                              {course.status === 'published' ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-gray-400" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/lessons/${course.id}`)}
                              title="Görüntüle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/instructor/courses/${course.id}/edit`)}
                              title="Düzenle"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(course)}
                              title="Sil"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz kurs eklemediniz</h3>
                <p className="text-gray-500 mb-6">
                  İlk kursunuzu oluşturarak öğrencilerinize ulaşmaya başlayın
                </p>
                <Button className="btn-primary" onClick={() => navigate('/instructor/courses/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Kursunuzu Oluşturun
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kursu Silmek İstediğinize Emin Misiniz?</AlertDialogTitle>
              <AlertDialogDescription>
                &ldquo;{deleteConfirm?.title}&rdquo; kursu kalıcı olarak silinecek. Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleDeleteCourse(deleteConfirm?.id)}
              >
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Submerchant Registration Modal */}
        <Dialog open={showSubmerchantModal} onOpenChange={setShowSubmerchantModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-purple-600" />
                Alt Üye İşyeri Kaydı
              </DialogTitle>
              <DialogDescription>
                Ders satışlarından ödeme alabilmek için aşağıdaki bilgileri doldurun. 
                Bu bilgiler iyzico tarafından doğrulanacaktır.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Type Selection */}
              <div className="space-y-2">
                <Label>İşyeri Türü</Label>
                <Select 
                  value={submerchantForm.submerchant_type}
                  onValueChange={(value) => setSubmerchantForm({...submerchantForm, submerchant_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tür seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERSONAL">Bireysel (Şahıs)</SelectItem>
                    <SelectItem value="PRIVATE_COMPANY">Şahıs Şirketi</SelectItem>
                    <SelectItem value="LIMITED_OR_JOINT_STOCK_COMPANY">Limited / Anonim Şirket</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">Ad Soyad *</Label>
                <Input
                  id="full_name"
                  value={submerchantForm.full_name}
                  onChange={(e) => setSubmerchantForm({...submerchantForm, full_name: e.target.value})}
                  placeholder="Adınız Soyadınız"
                />
              </div>

              {/* TC Kimlik No */}
              <div className="space-y-2">
                <Label htmlFor="identity_number">TC Kimlik No *</Label>
                <Input
                  id="identity_number"
                  value={submerchantForm.identity_number}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setSubmerchantForm({...submerchantForm, identity_number: val});
                  }}
                  placeholder="11 haneli TC Kimlik No"
                  maxLength={11}
                />
                <p className="text-xs text-gray-500">11 haneli olmalı</p>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">E-posta *</Label>
                <Input
                  id="email"
                  type="email"
                  value={submerchantForm.email}
                  onChange={(e) => setSubmerchantForm({...submerchantForm, email: e.target.value})}
                  placeholder="ornek@email.com"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="gsm_number">Telefon (Opsiyonel)</Label>
                <Input
                  id="gsm_number"
                  value={submerchantForm.gsm_number}
                  onChange={(e) => setSubmerchantForm({...submerchantForm, gsm_number: e.target.value})}
                  placeholder="+905xxxxxxxxx"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Adres *</Label>
                <Input
                  id="address"
                  value={submerchantForm.address}
                  onChange={(e) => setSubmerchantForm({...submerchantForm, address: e.target.value})}
                  placeholder="Tam adresiniz"
                />
              </div>

              {/* IBAN */}
              <div className="space-y-2">
                <Label htmlFor="iban">IBAN *</Label>
                <Input
                  id="iban"
                  value={submerchantForm.iban}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 26);
                    setSubmerchantForm({...submerchantForm, iban: val});
                  }}
                  placeholder="TR..."
                  maxLength={26}
                />
                <p className="text-xs text-gray-500">TR ile başlamalı, 26 karakter (boşluksuz)</p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Bilgi:</strong> Bu bilgiler iyzico tarafından doğrulanacaktır. 
                  Ders satışlarınızdan elde ettiğiniz gelirin %80&apos;i doğrudan IBAN&apos;ınıza aktarılır, 
                  %20&apos;si platform komisyonu olarak kesilir.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmerchantModal(false)}>
                İptal
              </Button>
              <Button 
                onClick={handleSubmerchantSubmit} 
                disabled={submerchantLoading}
                className="btn-primary"
              >
                {submerchantLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Kaydı Tamamla
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InstructorDashboard;
